import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, Mic, FileText, Clock } from "lucide-react";
import { Reveal, SectionTitle } from "../components/common";
import { publicApi, mediaUrl } from "../lib/api";
import GuideDownloadModal from "../components/GuideDownloadModal";
import { CaseStudyCard } from "../components/CaseStudyCard";
import Seo, { breadcrumbSchema } from "../components/Seo";
import ShareRow from "../components/ShareRow";

export default function Insights() {
  const [blog, setBlog] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [guides, setGuides] = useState([]);
  const [stories, setStories] = useState([]);
  const [activeGuide, setActiveGuide] = useState(null);

  useEffect(() => {
    publicApi.blog().then(setBlog).catch(() => {});
    publicApi.podcasts().then(setPodcasts).catch(() => {});
    publicApi.guides().then(setGuides).catch(() => {});
    publicApi.caseStudies().then(setStories).catch(() => {});
  }, []);

  // Somebody arriving from a shared guide link gets that guide's download open.
  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get("guide");
    if (!wanted || !guides.length) return;
    const match = guides.find((g) => g.slug === wanted || g.id === wanted);
    if (match && match.file_url) setActiveGuide(match);
  }, [guides]);

  return (
    <div>
      <Seo title="Insights & Resources — Business, Tax & Finance" description="Practical business knowledge from the CASS team: articles on tax, cash flow, VAT and growth, our podcast, and free downloadable guides to help your business thrive." jsonLd={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Insights", path: "/insights" }])} />

      <section className="numbers-pattern text-white">
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <span className="text-orange font-bold text-sm tracking-widest uppercase">Insights & Resources</span>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl leading-[0.95] mt-3">More than<br />numbers</h1>
          <p className="mt-5 text-slate-300 max-w-2xl leading-relaxed">Practical articles, podcast episodes and free guides to help you run a healthier, more profitable business. Our library grows all the time.</p>
        </div>
      </section>

      {/* Articles */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex items-end justify-between mb-10">
              <SectionTitle eyebrow="Articles" title="Latest from the blog" />
              <Link to="/blog" className="hidden md:inline-flex items-center gap-2 text-orange font-bold hover:gap-3 transition-all shrink-0 mb-12" data-testid="insights-all-articles">All articles <ArrowRight size={16} /></Link>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {blog.slice(0, 3).map((p, i) => (
              <Reveal key={p.id} delay={i * 80}>
                <Link to={`/blog/${p.slug}`} className="group block h-full bg-slate-50 rounded-2xl border border-navy/5 overflow-hidden hover:-translate-y-1 transition-all" data-testid={`insights-article-${i}`}>
                  <div className="aspect-[16/10] overflow-hidden bg-navy/5 rounded-xl m-3 mb-0">{p.image && <img loading="lazy" decoding="async" src={p.image} alt={p.image_alt || p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}</div>
                  <div className="p-6">
                    <span className="text-xs font-bold text-orange uppercase tracking-wider">{p.category}</span>
                    <h3 className="font-display font-bold text-lg text-navy mt-2 mb-2 leading-snug">{p.title}</h3>
                    <p className="text-sm text-slate-500 line-clamp-2">{p.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Podcast */}
      <section className="py-20 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex items-end justify-between mb-10">
              <SectionTitle eyebrow="Podcast" title="Recent episodes" />
              <Link to="/podcast" className="hidden md:inline-flex items-center gap-2 text-orange font-bold hover:gap-3 transition-all shrink-0 mb-12" data-testid="insights-all-podcasts">All episodes <ArrowRight size={16} /></Link>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {podcasts.slice(0, 3).map((ep, i) => (
              <Reveal key={ep.id} delay={i * 80}>
                <Link to="/podcast" className="group block h-full bg-white rounded-2xl border border-navy/5 shadow-sm overflow-hidden hover:-translate-y-1 transition-all" data-testid={`insights-podcast-${i}`}>
                  <div className="aspect-[16/10] overflow-hidden bg-navy flex items-center justify-center rounded-xl m-3 mb-0">{ep.image ? <img loading="lazy" decoding="async" src={ep.image} alt={ep.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <Mic size={40} className="text-orange" />}</div>
                  <div className="p-6">
                    <h3 className="font-display font-bold text-lg text-navy mb-2 leading-snug">{ep.title}</h3>
                    <p className="text-sm text-slate-500 line-clamp-2">{ep.description}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Client Success Stories */}
      {stories.length > 0 && (
        <section className="py-20 md:py-24 bg-white" data-testid="insights-success-stories">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal>
              <div className="flex items-end justify-between mb-10">
                <SectionTitle eyebrow="Client Success Stories" title="Real results, real businesses" />
                <Link to="/testimonials" className="hidden md:inline-flex items-center gap-2 text-orange font-bold hover:gap-3 transition-all shrink-0 mb-12" data-testid="insights-all-stories">All stories <ArrowRight size={16} /></Link>
              </div>
            </Reveal>
            <div className="grid md:grid-cols-3 gap-6">
              {stories.slice(0, 3).map((c, i) => (
                <Reveal key={c.id} delay={i * 80}>
                  <CaseStudyCard story={c} bg="bg-slate-50" testid={`insights-story-${i}`} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Guides */}
      <section id="free-resources" className="py-20 md:py-24 bg-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><SectionTitle eyebrow="Free resources" title="Guides worth downloading" subtitle="Practical, no-nonsense resources to help you make confident financial decisions." center /></Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {guides.map((d, i) => {
              if (!d.file_url) {
                return (
                  <Reveal key={d.id} delay={i * 100}>
                    <div className="h-full min-h-[200px] bg-navy rounded-2xl relative flex items-center justify-center overflow-hidden" data-testid={`insights-guide-${i}`}>
                      <div className="relative text-center px-6">
                        <Clock size={28} className="text-orange mx-auto mb-3" />
                        <p className="font-display font-extrabold text-2xl text-white uppercase tracking-wide">Coming Soon</p>
                        <p className="text-slate-300 text-sm mt-2">{d.title}</p>
                      </div>
                    </div>
                  </Reveal>
                );
              }
              return (
                <Reveal key={d.id} delay={i * 100}>
                  <div className="h-full bg-slate-50 rounded-2xl border border-navy/5 overflow-hidden p-8" data-testid={`insights-guide-${i}`}>
                    {d.image ? (
                      <div className="float-left w-1/2 sm:w-[47%] mr-5 mb-3 rounded-xl overflow-hidden bg-white border border-navy/5 aspect-[16/9] flex items-center justify-center">
                        <img loading="lazy" decoding="async" src={mediaUrl(d.image, 500)} alt={d.image_alt || d.title} className="w-full h-full object-contain" />
                      </div>
                    ) : (
                      <div className="float-left mr-5 mb-3 w-12 h-12 rounded-xl bg-orange/10 flex items-center justify-center text-orange"><FileText size={24} /></div>
                    )}
                    <h3 className="font-display font-bold text-xl text-navy mb-3">{d.title}</h3>
                    <p className="clear-both text-sm text-slate-500 leading-relaxed mb-6 pt-1">{d.description}</p>
                    <button onClick={() => setActiveGuide(d)} data-testid={`insights-guide-download-${i}`} className="clear-both inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-white font-bold text-sm px-6 py-3 rounded-full transition-all hover:-translate-y-0.5">Free Download <Download size={15} /></button>
                    <ShareRow
                      className="clear-both mt-4"
                      path={`/insights?guide=${d.slug || d.id}`}
                      title={d.title}
                      label="Share"
                    />
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <GuideDownloadModal guide={activeGuide} open={!!activeGuide} onClose={() => setActiveGuide(null)} />

      <section className="py-16 bg-navy text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display font-extrabold text-3xl md:text-4xl mb-4">Want tailored advice for your business?</h2>
          <p className="text-slate-300 mb-8">Insights are great — but nothing beats a conversation. Let's talk about where you want to take your business.</p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5" data-testid="insights-contact-cta">Let's Talk <ArrowRight size={18} /></Link>
        </div>
      </section>
    </div>
  );
}
